import type { AiEffort, AiGenParams, AiModelInfo, AiProtocol, AiProviderConfig, AiProviderId } from './types/ai';
import { AI_EFFORTS, AI_PROVIDER_IDS } from './types/ai';

export interface AiProviderSpec {
  label: string;
  protocol: AiProtocol;
  /** Base URL of the API root; empty for custom until an admin sets one. */
  defaultBaseUrl: string;
  /** Whether the provider accepts a reasoning-effort setting for at least some models. */
  supportsEffort: boolean;
  /** Where an admin obtains a key. */
  keysUrl: string;
  keyPrefixHint: string;
}

export const AI_PROVIDERS: Record<AiProviderId, AiProviderSpec> = {
  openai: { label: 'OpenAI', protocol: 'openai', defaultBaseUrl: 'https://api.openai.com/v1', supportsEffort: true, keysUrl: 'https://platform.openai.com/api-keys', keyPrefixHint: 'sk-…' },
  anthropic: { label: 'Anthropic (Claude)', protocol: 'anthropic', defaultBaseUrl: 'https://api.anthropic.com', supportsEffort: true, keysUrl: 'https://console.anthropic.com/settings/keys', keyPrefixHint: 'sk-ant-…' },
  google: { label: 'Google (Gemini)', protocol: 'google', defaultBaseUrl: 'https://generativelanguage.googleapis.com/v1beta', supportsEffort: true, keysUrl: 'https://aistudio.google.com/apikey', keyPrefixHint: 'AIza…' },
  azure: { label: 'Microsoft (Azure OpenAI)', protocol: 'openai', defaultBaseUrl: '', supportsEffort: true, keysUrl: 'https://portal.azure.com', keyPrefixHint: 'Azure OpenAI key (Keys and Endpoint)' },
  openrouter: { label: 'OpenRouter', protocol: 'openai', defaultBaseUrl: 'https://openrouter.ai/api/v1', supportsEffort: true, keysUrl: 'https://openrouter.ai/keys', keyPrefixHint: 'sk-or-…' },
  custom: { label: 'Custom (OpenAI-compatible)', protocol: 'openai', defaultBaseUrl: '', supportsEffort: false, keysUrl: '', keyPrefixHint: 'any' },
};

/** Providers whose API root must be entered by an admin. */
export const BASE_URL_PROVIDERS: AiProviderId[] = ['azure', 'openrouter', 'custom'];

/** Fallback model list shown before a provider's live list has been fetched. */
export const SEED_MODELS: Record<AiProviderId, AiModelInfo[]> = {
  openai: [
    { id: 'gpt-5', label: 'GPT-5', contextWindow: 400000, source: 'manual' },
    { id: 'gpt-5-mini', label: 'GPT-5 mini', contextWindow: 400000, source: 'manual' },
    { id: 'gpt-4.1', label: 'GPT-4.1', contextWindow: 1047576, source: 'manual' },
    { id: 'gpt-4o-mini', label: 'GPT-4o mini', contextWindow: 128000, source: 'manual' },
  ],
  anthropic: [
    { id: 'claude-sonnet-5', label: 'Claude Sonnet 5', contextWindow: 200000, source: 'manual' },
    { id: 'claude-opus-5', label: 'Claude Opus 5', contextWindow: 200000, source: 'manual' },
    { id: 'claude-fable-5-1', label: 'Claude Fable 5.1', contextWindow: 200000, source: 'manual' },
    { id: 'claude-haiku-4-5-20251001', label: 'Claude Haiku 4.5', contextWindow: 200000, source: 'manual' },
  ],
  google: [
    { id: 'gemini-2.5-pro', label: 'Gemini 2.5 Pro', contextWindow: 1048576, source: 'manual' },
    { id: 'gemini-2.5-flash', label: 'Gemini 2.5 Flash', contextWindow: 1048576, source: 'manual' },
  ],
  azure: [],
  openrouter: [
    { id: 'openai/gpt-5-mini', label: 'OpenAI: GPT-5 mini', contextWindow: 400000, source: 'manual' },
    { id: 'anthropic/claude-sonnet-5', label: 'Anthropic: Claude Sonnet 5', contextWindow: 200000, source: 'manual' },
  ],
  custom: [],
};

export const EFFORTS: { value: AiEffort; label: string }[] = [
  { value: 'none', label: 'None (fastest, cheapest)' },
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High (deepest reasoning)' },
];

export const DEFAULT_GEN_PARAMS: AiGenParams = { effort: 'none', temperature: 0.2, maxTokens: 1024, topP: 1 };

export function defaultProviderConfig(id: AiProviderId): AiProviderConfig {
  return {
    enabled: false, baseUrl: AI_PROVIDERS[id].defaultBaseUrl, keyConfigured: false, keyHint: '', keyUpdatedAt: null, keyUpdatedBy: null,
    defaultModel: SEED_MODELS[id][0]?.id ?? '', defaults: { ...DEFAULT_GEN_PARAMS }, models: SEED_MODELS[id], modelsRefreshedAt: null, lastTest: null,
  };
}

export function defaultProvidersSettings(): Record<AiProviderId, AiProviderConfig> {
  return Object.fromEntries(AI_PROVIDER_IDS.map((id) => [id, defaultProviderConfig(id)])) as Record<AiProviderId, AiProviderConfig>;
}

export const isAiProviderId = (v: unknown): v is AiProviderId => typeof v === 'string' && (AI_PROVIDER_IDS as readonly string[]).includes(v);
export const isAiEffort = (v: unknown): v is AiEffort => typeof v === 'string' && (AI_EFFORTS as readonly string[]).includes(v);

/** Heuristic for models that accept a reasoning-effort/thinking setting. Adapters still retry without it on rejection. */
export function modelSupportsEffort(provider: AiProviderId, model: string): boolean {
  const m = model.toLowerCase();
  switch (AI_PROVIDERS[provider].protocol) {
    case 'anthropic': return /claude-(opus|sonnet|fable|haiku)-(4|5)/.test(m) || /claude-3-7/.test(m);
    case 'google': return /gemini-(2\.5|3)/.test(m);
    default: return provider === 'openrouter' || /^(o\d|gpt-5)/.test(m.replace(/^openai\//, ''));
  }
}
